'use client';

import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowLeft, Pencil } from 'lucide-react';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { usePathname } from 'next/navigation';

import AppLoader from '@/components/ui/app-loader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { WorkspaceAccessStatus } from '@/features/referrals/components/common/workspace-access-status';
import { checkReferralAccess } from '@/features/referrals/services/referrals-services';
import { createWorkspace, updateWorkspaceImage } from '@/features/user-settings/services/workspace';

import { Avatar } from '@/shared/components';
import { ImageUploadDialog } from '@/shared/components/image-upload/image-upload-dialog';
import { LogoutLink } from '@/shared/components/logout-link';
import { toast } from '@/shared/components/toast';
import { extractDomainFromEmail, isBlockedDomain } from '@/shared/constants/blocked-domains';
import { COPY } from '@/shared/constants/copies';
import { MIXPANEL_APP_FEATURES, mixpanelEvents } from '@/shared/constants/mixpanel-events';
import { APP_ROUTES } from '@/shared/constants/navigation';
import { QUERY_KEY_INITIALS } from '@/shared/constants/query-key-initials';
import { useAppCallbacks, useAppStore } from '@/shared/contexts/app-wrapper/providers/app-provider';
import { discoverWorkspaces } from '@/shared/services/workspace/discover';
import { emitMixpanelEvent } from '@/shared/utils/mixpanel-utils';
import { KeyboardKeys } from '@/shared/utils/navigation';

import { matchesRoute } from '@/utils/common';

import {
	ONBOARDING_SCREEN_HEADERS,
	ONBOARDING_VIEW_WORKSPACE_JOIN,
	OnboardingScreen,
} from '../../constants/user-onboarding-constants';
import { useUserOnboarding } from '../../contexts/user-onboarding-provider';
import { ScreenLayout } from '../common/screen-layout';
import { WorkspaceJoinScreen } from './workspace-join-screen';

export function WorkspaceCreateScreen() {
	const { onboardingData, setWorkspaceName, goToNextScreen, source, skipLayout, notifyViewChange } =
		useUserOnboarding();
	const userEmail = useAppStore((state) => state.userDetails?.email);
	const isDomainBlocked = useMemo(
		() => isBlockedDomain(extractDomainFromEmail(userEmail ?? '')),
		[userEmail]
	);

	const [showCreateForm, setShowCreateForm] = useState(false);

	// Detect referral invite path (/:handle/refer) and extract the handle
	const pathname = usePathname();
	const referralHandle = useMemo(() => {
		if (!pathname || !matchesRoute(APP_ROUTES.REFERRAL_INVITE, pathname)) return null;
		return pathname.split('/')[1] ?? null;
	}, [pathname]);

	const {
		mutate: checkAccess,
		data: referralData,
		status: referralStatus,
	} = useMutation({
		mutationFn: () => checkReferralAccess(referralHandle!),
		onSuccess: (result) => {
			if (result.hasAccess) goToNextScreen();
		},
	});

	useEffect(() => {
		if (referralHandle) checkAccess();
		// eslint-disable-next-line react-hooks/exhaustive-deps -- fire once on mount
	}, []);

	const { data: discoverData, isLoading: isDiscoverLoading } = useQuery({
		queryKey: [QUERY_KEY_INITIALS.DISCOVER_WORKSPACES],
		queryFn: discoverWorkspaces,
		enabled: !isDomainBlocked && !referralHandle,
	});

	const discoverableWorkspaces = discoverData?.pages ?? [];
	const hasDiscoverableWorkspaces = discoverableWorkspaces.length > 0;

	const handleSuccess = useCallback(() => {
		goToNextScreen();
	}, [goToNextScreen]);

	// Fire "viewed create workspace" only when we actually show the create form
	useEffect(() => {
		if (isDiscoverLoading) return;
		if (hasDiscoverableWorkspaces && !showCreateForm) return;

		const isNewOnboardingFeature = source === MIXPANEL_APP_FEATURES.USER_ONBOARDING_VIA_MODAL;
		const event = isNewOnboardingFeature
			? mixpanelEvents.VIEWED_NEW_ONBOARDING_CREATE_WORKSPACE_SCREEN
			: mixpanelEvents.VIEWED_CREATE_WORKSPACE_SCREEN;

		emitMixpanelEvent(event, {
			feature: isNewOnboardingFeature
				? MIXPANEL_APP_FEATURES.USER_ONBOARDING_VIA_MODAL
				: MIXPANEL_APP_FEATURES.USER_ONBOARDING,
			type: source,
		});
	}, [isDiscoverLoading, hasDiscoverableWorkspaces, showCreateForm, source]);

	// Notify parent when view changes between join and create
	useEffect(() => {
		if (isDiscoverLoading) return;
		if (hasDiscoverableWorkspaces && !showCreateForm) {
			notifyViewChange(ONBOARDING_VIEW_WORKSPACE_JOIN);
		} else {
			notifyViewChange(OnboardingScreen.WORKSPACE_DETAILS);
		}
	}, [hasDiscoverableWorkspaces, showCreateForm, isDiscoverLoading, notifyViewChange]);

	// Handle referral invite path — show join state instead of create form
	if (referralHandle && !showCreateForm) {
		const isLoading = referralStatus === 'idle' || referralStatus === 'pending';
		const hasAccess = referralStatus === 'success' && referralData?.hasAccess;

		const statusIconState = isLoading ? 'loading' : hasAccess ? 'loading' : 'error';

		const content = (
			<WorkspaceAccessStatus
				status={statusIconState}
				loadingDescription="Checking workspace access…"
				workspaceName={referralData?.workspaceName ?? referralHandle}
				isAlreadyMember={referralData?.alreadyMember}
				errorDescription={COPY.onboarding.inviteInvalid}
				errorAction={
					<Button
						variant="outline"
						className="w-full"
						onClick={() => setShowCreateForm(true)}
					>
						Create a new workspace
					</Button>
				}
			/>
		);

		if (skipLayout) return content;

		return (
			<ScreenLayout
				title={hasAccess ? 'Joining workspace' : 'Set up your workspace'}
				cardContent={content}
				topRight={<LogoutLink />}
			/>
		);
	}

	// Show loading while discover API is in flight
	if (isDiscoverLoading) {
		if (skipLayout) {
			return (
				<div className="flex w-full items-center justify-center py-8">
					<AppLoader />
				</div>
			);
		}
		return (
			<div className="flex h-screen w-full items-center justify-center bg-slate-100">
				<AppLoader />
			</div>
		);
	}

	// Show join screen if discoverable workspaces exist and user hasn't clicked "create new"
	if (hasDiscoverableWorkspaces && !showCreateForm) {
		return (
			<WorkspaceJoinScreen
				workspaces={discoverableWorkspaces}
				onCreateNewClick={() => setShowCreateForm(true)}
			/>
		);
	}

	const formContent = (
		<WorkspaceCreationForm
			workspaceName={onboardingData.workspaceName}
			onWorkspaceNameChange={setWorkspaceName}
			onSuccess={handleSuccess}
			showImageUpload={true}
			defaultWorkspaceImage={onboardingData.workspacePhoto}
		/>
	);

	if (skipLayout) {
		return formContent;
	}

	return (
		<ScreenLayout
			title={ONBOARDING_SCREEN_HEADERS[OnboardingScreen.WORKSPACE_DETAILS].title}
			description={ONBOARDING_SCREEN_HEADERS[OnboardingScreen.WORKSPACE_DETAILS]?.description}
			cardContent={formContent}
			topLeft={
				hasDiscoverableWorkspaces ? (
					<button
						type="button"
						className="flex items-center gap-1 text-sm text-secondary hover:text-primary"
						onClick={() => setShowCreateForm(false)}
					>
						<ArrowLeft className="size-3.5" />
						Back
					</button>
				) : null
			}
			topRight={<LogoutLink />}
		/>
	);
}

interface WorkspaceCreationFormProps {
	workspaceName: string;
	onWorkspaceNameChange: (name: string) => void;
	onSuccess?: (workspaceId: string) => void;
	showImageUpload?: boolean;
	className?: string;
	buttonText?: string;
	loadingText?: string;
	defaultWorkspaceImage?: string;
}

export function WorkspaceCreationForm({
	workspaceName,
	onWorkspaceNameChange,
	onSuccess,
	showImageUpload = false,
	className = '',
	buttonText = 'Create workspace',
	loadingText = 'Creating...',
	defaultWorkspaceImage,
}: WorkspaceCreationFormProps) {
	const updateDefaultWorkspaceId = useAppCallbacks((state) => state.updateDefaultWorkspaceId);
	const workspaceNameInputRef = useRef<HTMLInputElement>(null);
	const [name, setName] = useState(workspaceName);
	const [imageUploadDialogOpen, setImageUploadDialogOpen] = useState(false);
	const [error, setError] = useState('');

	// Local state for image handling during workspace creation
	const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
	const [localImageUrl, setLocalImageUrl] = useState<string | null>(null);

	const createWorkspaceMutation = useMutation({
		mutationFn: createWorkspace,
		onSuccess: async (data) => {
			// Update the default workspace ID
			await updateDefaultWorkspaceId(data.id);

			// If there's a selected image file, upload it to the newly created workspace
			if (selectedImageFile) {
				try {
					const formData = new FormData();
					formData.append('image', selectedImageFile);
					await updateImageMutation.mutateAsync(formData);

					// Clear local image state
					clearLocalImageState();
				} catch (error) {
					toast({
						title: 'Workspace created but failed to upload logo',
						description: 'You can update the logo later in settings',
						variant: 'destructive',
					});
				}
			}

			emitMixpanelEvent(mixpanelEvents.COMPLETED_CREATE_WORKSPACE, {
				feature: MIXPANEL_APP_FEATURES.USER_ONBOARDING,
			});

			// Call the onSuccess callback with the workspace ID
			onSuccess?.(data.id);
		},
	});

	// Update workspace image mutation (for after workspace creation as we can only have workspaceId after creation)
	const updateImageMutation = useMutation({
		mutationFn: updateWorkspaceImage,
		onError: () => {
			toast({
				title: 'Failed to update logo',
				description: 'Please try again later',
				variant: 'destructive',
			});
		},
	});

	const isCreateWorkspaceLoading = createWorkspaceMutation.isPending;

	// Clear local image state and revoke object URL
	const clearLocalImageState = useCallback(() => {
		if (localImageUrl) {
			URL.revokeObjectURL(localImageUrl);
		}
		setSelectedImageFile(null);
		setLocalImageUrl(null);
	}, [localImageUrl]);

	const validateAndCreateWorkspace = useCallback(
		(workspaceName: string) => {
			if (isCreateWorkspaceLoading) return;

			if (!workspaceName) {
				setError('Please enter a workspace name');
				return;
			}

			if (workspaceName.length < 2) {
				setError('Workspace name must be at least 2 characters long');
				return;
			}

			if (workspaceName.length > 50) {
				setError('Workspace name must be less than 50 characters');
				return;
			}

			setError('');

			createWorkspaceMutation.mutate({
				name: workspaceName,
			});
		},
		[isCreateWorkspaceLoading]
	);

	const handleWorkspaceNameChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			if (isCreateWorkspaceLoading) return;

			const value = e.target.value;
			setName(value);
			onWorkspaceNameChange?.(value);

			if (error) setError('');
		},
		[isCreateWorkspaceLoading, onWorkspaceNameChange, error, setName]
	);

	const handleInputWorkspaceNameKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLInputElement>) => {
			if (isCreateWorkspaceLoading) return;
			if (e.key === KeyboardKeys.Enter) {
				e.preventDefault();
				validateAndCreateWorkspace(name.trim());
			}
		},
		[isCreateWorkspaceLoading, validateAndCreateWorkspace, name]
	);

	const handleSubmitWorkspaceName = useCallback(
		(e: React.FormEvent<HTMLFormElement>) => {
			e.preventDefault();
			if (isCreateWorkspaceLoading) return;
			validateAndCreateWorkspace(name.trim());
		},
		[isCreateWorkspaceLoading, validateAndCreateWorkspace, name]
	);

	const handleImageUpload = useCallback(
		async (file: File) => {
			clearLocalImageState();
			setSelectedImageFile(file);

			const objectUrl = URL.createObjectURL(file);
			setLocalImageUrl(objectUrl);
		},
		[clearLocalImageState]
	);

	const handleRemoveImage = useCallback(async () => {
		clearLocalImageState();
	}, [clearLocalImageState]);

	const isWorkspaceNameValid = useMemo(() => {
		const trimmedName = name.trim();
		return trimmedName.length >= 2 && trimmedName.length <= 50 && !error;
	}, [name, error]);

	// Determine which image to show in avatar
	const finalDisplayImageUrl = useMemo(() => {
		// Priority: local preview > prop displayImageUrl
		return localImageUrl || defaultWorkspaceImage || '';
	}, [localImageUrl, defaultWorkspaceImage]);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			if (localImageUrl) {
				URL.revokeObjectURL(localImageUrl);
			}
		};
	}, [localImageUrl]);

	useEffect(() => {
		if (workspaceNameInputRef.current) {
			workspaceNameInputRef.current.focus();
		}
	}, []);

	return (
		<>
			<form
				onSubmit={handleSubmitWorkspaceName}
				className={`space-y-6 ${className}`}
			>
				{/* Workspace Logo Section */}
				{showImageUpload && (
					<div className="flex flex-col space-y-2">
						<Label className="text-sm font-medium">Logo (optional)</Label>
						<div className="flex justify-start">
							<button
								type="button"
								className="group relative cursor-pointer border-none outline-none"
								onClick={() => setImageUploadDialogOpen(true)}
								disabled={isCreateWorkspaceLoading}
							>
								<Avatar
									imageUrl={finalDisplayImageUrl}
									name={name || 'W'}
									className="size-[48px] rounded-lg"
									fallbackClassName="rounded-lg"
								/>

								{/* Hover overlay with centered edit button */}
								<div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/50 opacity-0 transition-opacity group-hover:opacity-100">
									{isCreateWorkspaceLoading ? (
										<AppLoader className="size-4 text-white" />
									) : (
										<Pencil className="size-5 text-white" />
									)}
								</div>
							</button>
						</div>
					</div>
				)}

				{/* Workspace Name Section */}
				<div className="w-full space-y-1">
					<Label
						htmlFor="workspace-name"
						className="text-sm font-medium"
					>
						Workspace name
					</Label>
					<Input
						ref={workspaceNameInputRef}
						id="workspace-name"
						name="workspace-name"
						type="text"
						placeholder="Type here..."
						value={name}
						onChange={handleWorkspaceNameChange}
						onKeyDown={handleInputWorkspaceNameKeyDown}
						maxLength={50}
						disabled={isCreateWorkspaceLoading}
					/>
					<div className="flex flex-col items-start justify-between">
						{error ? (
							<p className="text-sm font-light text-red-500">{error}</p>
						) : (
							<span className="text-sm text-secondary">{COPY.onboarding.workspaceNameHelp}</span>
						)}
					</div>
				</div>

				<Button
					type="submit"
					className="w-full"
					disabled={!isWorkspaceNameValid || isCreateWorkspaceLoading}
				>
					{isCreateWorkspaceLoading ? (
						<>
							<AppLoader className="mr-2 h-4 w-4" />
							{loadingText}
						</>
					) : (
						buttonText
					)}
				</Button>
			</form>

			{showImageUpload && (
				<ImageUploadDialog
					open={imageUploadDialogOpen}
					onOpenChange={setImageUploadDialogOpen}
					onImageUpload={handleImageUpload}
					currentImageUrl={finalDisplayImageUrl}
					onImageRemove={handleRemoveImage}
					aspectRatio={1}
				/>
			)}
		</>
	);
}
